<template>
  <my-giving-layout :enable-scroll-event="true">
    <div class="hogc-theme-campaign bg-campaign-surface w-full min-h-full flex flex-col">
      <PledgeHeader :back="back">I want to be a part<br />of history</PledgeHeader>
      <div class="flex-1 px-[16px] md:px-[24px] lg:px-[28px] pt-[24px] pb-[20px]">
        <div class="w-full md:max-w-2xl md:mx-auto">
          <!-- A first-timer's tick shows on C1 (months) only, as the frames draw it. -->
          <PledgeSummary v-if="summary" :summary="summary" :show-first-time="summary.total > 0" />
        </div>
      </div>
      <!-- Pinned to the bottom of the screen: sticky in ion-content, which scrolls,
           and at the bottom of this full-height column when the cards are short. -->
      <div
        class="sticky bottom-0 z-10 bg-campaign-surface px-[16px] md:px-[24px] lg:px-[28px] pt-[12px] pb-[max(24px,env(safe-area-inset-bottom))]"
        data-test="bf-confirm-footer"
      >
        <div class="w-full md:max-w-2xl md:mx-auto">
          <PledgeSlideToSubmit
            label="Submit pledge"
            :loading="submitting"
            :celebrate="celebrate"
            :done="submitted"
            data-test="bf-confirm-slide"
            @submit="submit"
          >
            Submit pledge
          </PledgeSlideToSubmit>
        </div>
      </div>
    </div>
    <!-- The celebration's way out: the heart pops, then this navy rises over the page,
         and the submitted page opens under the same navy with the video on it. -->
    <div
      v-if="celebrate"
      :class="[
        'pointer-events-none fixed inset-0 z-[100] bg-campaign-primary transition-opacity duration-[400ms] ease-in',
        veiled ? 'opacity-100' : 'opacity-0',
      ]"
      aria-hidden="true"
      data-test="bf-confirm-veil"
    />
  </my-giving-layout>
</template>

<script>
import MyGivingLayout from '@/components/MyGivingLayout.vue';
import PledgeHeader from '@/components/MyGiving/BuildingFundPledge/PledgeHeader.vue';
import PledgeSlideToSubmit from '@/components/MyGiving/BuildingFundPledge/PledgeSlideToSubmit.vue';
import PledgeSummary from '@/components/MyGiving/BuildingFundPledge/PledgeSummary.vue';
import { BF_PLEDGE_ROUTES } from '@/constants/MyGiving/building-fund-pledge';
import { isBfCelebration, pledgeSummary } from '@/utils/building-fund-pledge';
import { triggerNotification } from '@/utils/haptics';
import { replaceWithoutTransition } from '@/utils/replace-without-transition';

// With the celebration: how long the heart has before the navy rises over the page,
// and before the page goes under it (the veil's 400ms fade plus a beat).
const HEART_MS = 600;
const VEIL_MS = 450;

// C1 and C2 of the BF2026 digital pledge: the pledge as it will be sent — five
// months, sow, or a one-time amount (C1 copy 2) — for the member to check before
// sliding to submit. The form left the body in the store's `pending`; the route
// guard sends anyone without one to the form.
export default {
  name: 'BuildingFundPledgeConfirm',

  components: { MyGivingLayout, PledgeHeader, PledgeSlideToSubmit, PledgeSummary },

  // Ionic's router, for the one navigation that sets its own animation. Only an
  // <ion-router-outlet> provides it, hence the default (see DirectedNavigation).
  inject: {
    navManager: { default: null },
  },

  data() {
    return {
      summary: null,
      submitting: false,
      // Accepted by giving-api; with the celebration, the slider's heart shows.
      submitted: false,
      celebrate: false,
      veiled: false,
      timers: [],
    };
  },

  // A copy, taken on every entry, as on the done page: a successful send clears
  // `pending`, which must not blank this page while it transitions out.
  ionViewWillEnter() {
    const pending = this.$store.state.buildingFundPledge.pending;
    if (pending) this.summary = pledgeSummary(pending);
    this.celebrate = isBfCelebration();
    this.submitted = false;
    this.veiled = false;
  },

  beforeUnmount() {
    this.timers.forEach(clearTimeout);
  },

  methods: {
    // The form was pushed under this page with everything the member typed, so
    // going back is a pop. The pledge is theirs to change there, and Continue
    // keeps a fresh one.
    back() {
      this.$store.commit('buildingFundPledge/setPending', null);
      this.$router.back();
    },

    async submit() {
      if (this.submitting) return;
      this.submitting = true;
      // The store shows any failure as a toast. Accepted, the slider stays at the
      // end while the page goes; replaced, so the member cannot come back and send
      // it again. Otherwise it goes back to the start for another try.
      const accepted = await this.$store.dispatch('buildingFundPledge/confirmPledge');
      if (!accepted) {
        this.submitting = false;
        return;
      }
      if (!this.celebrate) {
        this.$router.replace(BF_PLEDGE_ROUTES.done);
        return;
      }
      // Only now, with the pledge accepted: the heart and "Pledge submitted", a
      // success buzz, then the navy, then the submitted page and its video.
      this.submitted = true;
      triggerNotification('success');
      this.timers.push(
        setTimeout(() => {
          this.veiled = true;
        }, HEART_MS),
        // Under the navy, with no page slide to show through it.
        setTimeout(
          () => replaceWithoutTransition(this.navManager, this.$router, BF_PLEDGE_ROUTES.done),
          HEART_MS + VEIL_MS,
        ),
      );
    },
  },
};
</script>
